/**
 * Market Management (plan §18 "Market Management", "Sport setup"):
 * editable category tree, discovery subscriptions, league/tournament
 * selector from venue discovery data, compatibility status. Category labels
 * are organisational only — instrument identities never change.
 */
import { useMemo, useState } from "react";
import { Alert, Badge, Button, Group, Modal, MultiSelect, Paper, Select, Stack, Table, Text, TextInput, Title, Tree, type TreeNodeData } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { upgradeApi, type CatalogCategory } from "../api/fleet";

const LAUNCH_TYPES = ["game_winner", "match_winner"];
const EXTENSION_TYPES = ["spread", "total", "prop", "first_to_score"];

function buildTree(categories: CatalogCategory[], extras: Record<string, string[]>): TreeNodeData[] {
  const byParent = new Map<string | null, CatalogCategory[]>();
  for (const c of categories) {
    if (c.archived_at) continue;
    const list = byParent.get(c.parent_id) ?? [];
    list.push(c);
    byParent.set(c.parent_id, list);
  }
  const node = (c: CatalogCategory): TreeNodeData => ({
    value: c.id,
    label: `${c.label}${extras[c.id]?.length ? `  · ${(extras[c.id] ?? []).join(", ")}` : ""}`,
    children: [...(byParent.get(c.id) ?? []).map(node)],
  });
  return (byParent.get(null) ?? []).map(node);
}

export function MarketManagementPage() {
  const qc = useQueryClient();
  const tree = useQuery({ queryKey: ["catalog", "tree"], queryFn: () => upgradeApi.catalog.tree() });
  const competitions = useQuery({ queryKey: ["catalog", "competitions"], queryFn: () => upgradeApi.catalog.competitions() });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["catalog"] });
  const toast = (title: string, err?: unknown) => notifications.show({ color: err ? "red" : "green", title, message: err ? (err as Error).message : "" });

  const [selected, setSelected] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [newKind, setNewKind] = useState("folder");
  const [rename, setRename] = useState("");
  const [moveTo, setMoveTo] = useState<string | null>(null);
  const [movePreview, setMovePreview] = useState<Record<string, unknown> | null>(null);

  const extras = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const p of tree.data?.product_families ?? []) (out[p.category_id] ??= []).push(p.legacy_market_id ?? p.label);
    for (const c of tree.data?.competitions ?? []) if (c.category_id) (out[c.category_id] ??= []).push(c.label);
    for (const pol of tree.data?.category_policies ?? []) if (pol.paused) (out[pol.scope_id] ??= []).push("PAUSED");
    return out;
  }, [tree.data]);
  const current = tree.data?.categories.find((c) => c.id === selected) ?? null;

  const createCategory = useMutation({
    mutationFn: () => upgradeApi.catalog.createCategory({ parent_id: selected!, label: newLabel, kind: newKind }),
    onSuccess: () => { toast("Category created"); setNewLabel(""); refresh(); },
    onError: (e) => toast("Create failed", e),
  });
  const renameCategory = useMutation({
    mutationFn: () => upgradeApi.catalog.updateCategory(selected!, { label: rename, expected_revision: current?.revision }),
    onSuccess: () => { toast("Renamed (instrument ids unchanged)"); refresh(); },
    onError: (e) => toast("Rename failed", e),
  });
  const previewMove = useMutation({ mutationFn: () => upgradeApi.catalog.movePreview(selected!, moveTo!), onSuccess: setMovePreview, onError: (e) => toast("Preview failed", e) });
  const applyMove = useMutation({
    mutationFn: () => upgradeApi.catalog.updateCategory(selected!, { parent_id: moveTo!, expected_revision: current?.revision }),
    onSuccess: () => { toast("Moved"); setMovePreview(null); refresh(); },
    onError: (e) => toast("Move failed", e),
  });
  const archive = useMutation({ mutationFn: () => upgradeApi.catalog.archiveCategory(selected!), onSuccess: () => { toast("Archived"); refresh(); }, onError: (e) => toast("Archive refused", e) });

  // Sport setup: league/tournament selector + subscription creation.
  const [subOpen, setSubOpen] = useState(false);
  const [sport, setSport] = useState<string | null>(null);
  const [competitionId, setCompetitionId] = useState<string | null>(null);
  const [subLabel, setSubLabel] = useState("");
  const [types, setTypes] = useState<string[]>(LAUNCH_TYPES);
  const [follow, setFollow] = useState(true);
  const [editions, setEditions] = useState<string[]>([]);
  const sports = useMemo(() => [...new Set((competitions.data ?? []).map((c) => c.sport))].sort(), [competitions.data]);
  const leagueOptions = (competitions.data ?? []).filter((c) => !sport || c.sport === sport).map((c) => ({ value: c.id, label: `${c.label} (${c.venue_competition_id ?? "manual"})` }));
  const editionOptions = (competitions.data ?? []).find((c) => c.id === competitionId)?.editions?.map((e) => ({ value: e.id, label: e.label })) ?? [];
  const createSub = useMutation({
    mutationFn: () => upgradeApi.catalog.createSubscription({ label: subLabel, competition_id: competitionId, contract_types: types, follow_recurring: follow, edition_ids: editions }),
    onSuccess: () => { toast("Subscription created — collection starts on next discovery pass"); setSubOpen(false); refresh(); },
    onError: (e) => toast("Subscription refused", e),
  });
  const [statusPreview, setStatusPreview] = useState<{ id: string; status: string; preview: Record<string, unknown> } | null>(null);
  const previewStatus = useMutation({
    mutationFn: (p: { id: string; status: string }) => upgradeApi.catalog.subscriptionStatusPreview(p.id, p.status).then((preview) => ({ ...p, preview })),
    onSuccess: setStatusPreview,
  });
  const setStatus = useMutation({
    mutationFn: (p: { id: string; status: string; remove_assignments?: boolean }) => upgradeApi.catalog.setSubscriptionStatus(p.id, p),
    onSuccess: () => { toast("Subscription status updated"); setStatusPreview(null); refresh(); },
    onError: (e) => toast("Status change refused", e),
  });

  return (
    <>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Market Management</Title>
        <Button onClick={() => setSubOpen(true)}>Add sports subscription</Button>
      </Group>
      <Group align="start" grow>
        <Paper withBorder p="md">
          <Text fw={600} mb="xs">Hierarchy</Text>
          <Text size="xs" c="dimmed" mb="sm">Organisational only: renaming or moving a node never changes an instrument id. External ids identify instruments.</Text>
          {tree.data && (
            <Tree data={buildTree(tree.data.categories, extras)} selectOnClick
              renderNode={({ node, expanded, hasChildren, elementProps }) => (
                <Group gap={5} {...elementProps} onClick={(e) => { elementProps.onClick(e); setSelected(node.value); setRename(tree.data!.categories.find((c) => c.id === node.value)?.label ?? ""); }}>
                  {hasChildren && <span style={{ width: 12 }}>{expanded ? "▾" : "▸"}</span>}
                  <Text size="sm" fw={node.value === selected ? 700 : 400}>{node.label}</Text>
                </Group>
              )} />
          )}
        </Paper>
        <Paper withBorder p="md">
          <Text fw={600} mb="xs">{current ? `${current.label} · ${current.kind} · rev ${current.revision}` : "Select a node"}</Text>
          {current && (
            <Stack>
              <Group align="end">
                <TextInput label="Rename" value={rename} onChange={(e) => setRename(e.currentTarget.value)} />
                <Button variant="light" onClick={() => renameCategory.mutate()} disabled={current.kind === "root"}>Save</Button>
              </Group>
              <Group align="end">
                <TextInput label="New child label" value={newLabel} onChange={(e) => setNewLabel(e.currentTarget.value)} />
                <Select label="Kind" data={["folder", "asset_class", "asset", "timeframe", "sport", "league", "contract_type"]} value={newKind} onChange={(v) => setNewKind(v ?? "folder")} w={150} />
                <Button variant="light" onClick={() => createCategory.mutate()} disabled={!newLabel}>Add child</Button>
              </Group>
              <Group align="end">
                <Select label="Move under" data={(tree.data?.categories ?? []).filter((c) => c.id !== current.id && !c.archived_at).map((c) => ({ value: c.id, label: c.label }))} value={moveTo} onChange={setMoveTo} searchable w={260} />
                <Button variant="light" onClick={() => previewMove.mutate()} disabled={!moveTo}>Preview impact</Button>
              </Group>
              {movePreview && (
                <Alert color="blue" title="Move preview (inherited controls)">
                  <Text size="xs">Inherited pauses before: {JSON.stringify(movePreview.inherited_pauses_before)} → after: {JSON.stringify(movePreview.inherited_pauses_after)}</Text>
                  <Text size="xs">Affected: {String(movePreview.affected_product_families)} product families, {String(movePreview.affected_competitions)} competitions, {String(movePreview.affected_subscriptions)} subscriptions. Instrument ids unchanged.</Text>
                  <Button mt="xs" size="xs" onClick={() => applyMove.mutate()}>Apply move</Button>
                </Alert>
              )}
              <Button color="red" variant="subtle" onClick={() => archive.mutate()} disabled={current.kind === "root"}>Archive category</Button>
            </Stack>
          )}
        </Paper>
      </Group>

      <Title order={4} mt="xl" mb="xs">Discovery subscriptions</Title>
      <Paper withBorder p="xs">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Label</Table.Th><Table.Th>Kind</Table.Th><Table.Th>Contract types</Table.Th><Table.Th>Status</Table.Th><Table.Th>Health</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {(tree.data?.subscriptions ?? []).map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td>{s.label}</Table.Td>
                <Table.Td>{s.kind}</Table.Td>
                <Table.Td>{s.contract_types.map((t) => <Badge key={t} size="xs" mr={4} color={LAUNCH_TYPES.includes(t) || t === "updown" ? "green" : "gray"}>{t}</Badge>)}</Table.Td>
                <Table.Td><Badge color={s.status === "active" ? "green" : s.status === "disabled" ? "yellow" : "gray"}>{s.status}</Badge></Table.Td>
                <Table.Td><Text size="xs">{s.health?.last_discovery_at ? `discovered ${new Date(String(s.health.last_discovery_at)).toLocaleTimeString()} · ${String(s.health.instruments_tracked ?? 0)} tracked` : "no discovery yet"}{s.health?.last_error ? ` · error: ${String(s.health.last_error)}` : ""}</Text></Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {s.status !== "active" && <Button size="compact-xs" variant="light" onClick={() => setStatus.mutate({ id: s.id, status: "active" })}>Enable</Button>}
                    {s.status === "active" && <Button size="compact-xs" variant="light" color="yellow" onClick={() => previewStatus.mutate({ id: s.id, status: "disabled" })}>Disable…</Button>}
                    {s.status !== "archived" && <Button size="compact-xs" variant="light" color="gray" onClick={() => previewStatus.mutate({ id: s.id, status: "archived" })}>Archive…</Button>}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Modal opened={subOpen} onClose={() => setSubOpen(false)} title="Sport setup — new collection subscription" size="lg">
        <Stack>
          <Text size="sm" c="dimmed">Leagues and tournaments come from venue discovery; no tag ids to look up. Only winner contracts are tradable at launch — other types are tracked as extension points.</Text>
          <Select label="Sport" data={sports} value={sport} onChange={(v) => { setSport(v); setCompetitionId(null); }} searchable />
          <Select label="League / tournament" data={leagueOptions} value={competitionId} onChange={setCompetitionId} searchable />
          {editionOptions.length > 0 && <MultiSelect label="Seasons / editions (empty = follow recurring)" data={editionOptions} value={editions} onChange={setEditions} />}
          <MultiSelect label="Contract types" data={[...LAUNCH_TYPES.map((t) => ({ value: t, label: `${t} (supported)` })), ...EXTENSION_TYPES.map((t) => ({ value: t, label: `${t} (extension point — refused)` }))]} value={types} onChange={setTypes} />
          <Select label="Follow new editions automatically" data={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} value={follow ? "yes" : "no"} onChange={(v) => setFollow(v !== "no")} />
          <TextInput label="Label" value={subLabel} onChange={(e) => setSubLabel(e.currentTarget.value)} />
          <Button onClick={() => createSub.mutate()} disabled={!competitionId || !subLabel} loading={createSub.isPending}>Create subscription</Button>
        </Stack>
      </Modal>

      <Modal opened={!!statusPreview} onClose={() => setStatusPreview(null)} title={`Confirm: ${statusPreview?.status}`}>
        {statusPreview && (
          <Stack>
            <Text size="sm">Tracked instruments: {String(statusPreview.preview.tracked_instruments)} · still required elsewhere: {String(statusPreview.preview.instruments_still_required_elsewhere)} · open positions on these instruments: {String(statusPreview.preview.open_positions_on_instruments)}</Text>
            <Text size="sm">Dependent trading assignments: {(statusPreview.preview.affected_assignments as unknown[]).length}</Text>
            <Text size="xs" c="dimmed">Tracking needed by open positions/resolution is retained. Historical data is never deleted by this action.</Text>
            <Group>
              <Button color="yellow" onClick={() => setStatus.mutate({ id: statusPreview.id, status: statusPreview.status })}>Apply (fails if assignments depend on it)</Button>
              <Button color="red" variant="light" onClick={() => setStatus.mutate({ id: statusPreview.id, status: statusPreview.status, remove_assignments: true })}>Apply and remove dependent assignments</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
